import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { Copy, ExternalLink, Eye, Pencil, Play, Square, Trash2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import {
  ApprovalPolicies,
  ChannelPolicies,
  GenerationModes,
  OPEN_TRACKING_NOTE,
  SEND_ERROR_LABELS,
  formatDateTime,
  formatNumber,
  formatRate,
  messages,
  type CampaignSummary,
  type CampaignTarget,
  type Preflight,
  type SendErrorCode,
  type TrackingLinkClicks,
  type TrackingStats,
} from '@nesc/shared';
import { api, errorMessage, type ListResponse, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { usePollingInterval, useSelection } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SkeletonRows } from '@/components/ui/skeleton';
import { ResponsiveList, type Column } from '@/components/common/responsive-list';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfirmDialog, EmptyState, JobProgress } from '@/components/common/feedback';
import { BulkBar, PageHeader } from '@/components/common/page';
import { SendAttemptDialog } from '@/features/campaigns/send-attempt-dialog';
import { CampaignRenameDialog } from '@/features/campaigns/rename-dialog';
import { cn, tabTriggerClass } from '@/lib/utils';

const TABS = [
  { value: 'targets', label: messages.campaign.tabTargets },
  { value: 'unknown', label: messages.campaign.tabUnknown },
  { value: 'needs_action', label: messages.campaign.tabNeedsAction },
  { value: 'messages', label: messages.campaign.tabMessages },
  { value: 'tracking', label: messages.campaign.tabTracking },
  { value: 'settings', label: messages.campaign.tabSettings },
];

const STATE_FILTERS = [
  { value: '', label: 'すべて' },
  { value: 'done', label: '送信済' },
  { value: 'pending,holding', label: '待機' },
  { value: 'skipped', label: 'スキップ' },
  { value: 'needs_action', label: '要対応' },
];

/** S42 キャンペーン詳細 */
export const CampaignDetailPage = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'targets';
  const [stateFilter, setStateFilter] = useState('');
  const [pauseOpen, setPauseOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [sendNowOpen, setSendNowOpen] = useState(false);
  const [attemptId, setAttemptId] = useState<string | null>(null);

  const { data: campaign, isLoading } = useQuery({
    queryKey: qk.campaign(id),
    queryFn: () => api.get<SingleResponse<CampaignSummary & Record<string, unknown>>>(`/campaigns/${id}`),
  });

  const isRunning = campaign?.data.status === 'running';

  const { data: progress } = useQuery({
    queryKey: qk.campaignProgress(id),
    queryFn: () =>
      api.get<SingleResponse<{ status: string; counts: CampaignSummary['counts']; todaySent: number; dailyLimit: number; nextRunAt: string | null }>>(
        `/campaigns/${id}/progress`,
      ),
    refetchInterval: usePollingInterval(!!isRunning, 3000),
  });

  const targetState =
    tab === 'unknown' ? 'done' : tab === 'needs_action' ? 'needs_action' : stateFilter;

  const { data: targets, isLoading: targetsLoading } = useQuery({
    queryKey: qk.campaignTargets(id, { state: targetState, tab }),
    queryFn: () =>
      api.get<ListResponse<CampaignTarget & { skipReasonLabel: string | null }>>(
        `/campaigns/${id}/targets`,
        {
          state: targetState || undefined,
          result: tab === 'unknown' ? 'unknown' : undefined,
          limit: 100,
        },
      ),
    enabled: tab === 'targets' || tab === 'unknown' || tab === 'needs_action',
    refetchInterval: usePollingInterval(!!isRunning, 5000),
  });

  const pause = useMutation({
    mutationFn: () => api.post(`/campaigns/${id}/pause`, {}),
    onSuccess: () => {
      toast.success('一時停止しました');
      void queryClient.invalidateQueries({ queryKey: qk.campaign(id) });
      setPauseOpen(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const resume = useMutation({
    mutationFn: () => api.post(`/campaigns/${id}/resume`),
    onSuccess: () => {
      toast.success('再開しました');
      void queryClient.invalidateQueries({ queryKey: qk.campaign(id) });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/campaigns/${id}`),
    onSuccess: () => {
      toast.success('キャンペーンを削除しました');
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      navigate('/campaigns', { replace: true });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const currentStatus = progress?.data.status ?? campaign?.data.status ?? '';
  const isDraftLike = currentStatus === 'draft' || currentStatus === 'scheduled';

  // 開始前は対象が確定していないため、送信前チェックの内訳を見せる
  const { data: preflight, isLoading: preflightLoading } = useQuery({
    queryKey: ['campaigns', id, 'preflight', 'detail'],
    queryFn: () => api.post<SingleResponse<Preflight>>(`/campaigns/${id}/preflight`, {}),
    enabled: !!campaign && isDraftLike && (sendNowOpen || tab === 'targets'),
  });

  const sendNow = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ id: string; status: string; targets?: number }>>(`/campaigns/${id}/send-now`, {
        ...(isDraftLike && preflight ? { confirmToken: preflight.data.confirmToken } : {}),
      }),
    onSuccess: (result) => {
      toast.success(messages.campaign.sendNowStarted(result.data.targets ?? 0));
      setSendNowOpen(false);
      void queryClient.invalidateQueries({ queryKey: qk.campaign(id) });
      void queryClient.invalidateQueries({ queryKey: qk.campaignProgress(id) });
      void queryClient.invalidateQueries({ queryKey: ['campaigns', id, 'targets'] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns', id, 'preflight', 'detail'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (isLoading || !campaign) return <SkeletonRows rows={6} />;

  const c = campaign.data;
  const counts = progress?.data.counts ?? c.counts;

  const setTab = (value: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    }, { replace: true });

  const columns: Column<CampaignTarget & { skipReasonLabel: string | null }>[] = [
    {
      key: 'company',
      header: '企業名',
      render: (row) => (
        <button
          type="button"
          className="truncate text-left font-semibold underline-offset-2 hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/companies/${row.companyId}`);
          }}
        >
          {row.companyName}
        </button>
      ),
    },
    {
      key: 'state',
      header: '状態',
      width: '160px',
      render: (row) => (
        <div className="space-y-1">
          {row.lastResult ? (
            <StatusBadge kind="send_result" value={row.lastResult} />
          ) : (
            <StatusBadge kind="target_state" value={row.state} />
          )}
          {row.skipReasonLabel && (
            <p className="text-note text-foreground-muted">↳ {row.skipReasonLabel}</p>
          )}
          {row.lastErrorCode && (
            <p className="text-note text-foreground-muted">
              ↳ {SEND_ERROR_LABELS[row.lastErrorCode as SendErrorCode] ?? row.lastErrorCode}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'channel',
      header: 'チャネル',
      width: '110px',
      render: (row) => (row.channel ? <StatusBadge kind="channel" value={row.channel} /> : '—'),
    },
    {
      key: 'lastAttemptAt',
      header: '日時',
      width: '140px',
      render: (row) => <span className="tabular">{formatDateTime(row.lastAttemptAt)}</span>,
    },
  ];

  return (
    <>
      <PageHeader
        back="/campaigns"
        title={c.name}
        badge={
          <>
            <StatusBadge kind="campaign_status" value={progress?.data.status ?? c.status} size="lg" />
            {hasRole('manager') && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setRenameOpen(true)}
                aria-label={messages.campaign.rename}
                title={messages.campaign.rename}
              >
                <Pencil aria-hidden className="size-4" />
              </Button>
            )}
          </>
        }
        description={
          <>
            商材 {c.product?.name ?? '—'} / リスト {c.list?.name ?? '—'} /{' '}
            {ChannelPolicies.label(c.channelPolicy)} / {ApprovalPolicies.label(c.approvalPolicy)}
          </>
        }
        actions={
          <>
            {/* 即時配信：時間帯と 1 日上限を無視して今から送る。下書きは送信前チェックを見せてから開始する */}
            {hasRole('manager') && ['draft', 'scheduled', 'running', 'paused'].includes(currentStatus) && (
              <Button onClick={() => setSendNowOpen(true)}>
                <Zap aria-hidden />
                {messages.campaign.sendNow}
              </Button>
            )}
            {/* 下書きは実行設定（開始日時・上限・時間帯）を決めてから開始する道も残す */}
            {hasRole('manager') && currentStatus === 'draft' && (
              <Button variant="outline" onClick={() => navigate(`/campaigns/new?campaignId=${id}&step=3`)}>
                <Play aria-hidden />
                {messages.campaign.openSettings}
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate(`/campaigns/${id}/preview`)}>
              <Eye aria-hidden />
              {messages.campaign.preview}
            </Button>
            {hasRole('manager') && (
            <>
              {(progress?.data.status ?? c.status) === 'running' && (
                <Button variant="outline" onClick={() => setPauseOpen(true)}>
                  <Square aria-hidden />
                  {messages.campaign.pause}
                </Button>
              )}
              {(progress?.data.status ?? c.status) === 'paused' && (
                <Button onClick={() => resume.mutate()}>{messages.campaign.resume}</Button>
              )}
              {['draft', 'canceled', 'completed'].includes(progress?.data.status ?? c.status) && (
                <Button variant="outline" onClick={() => setDeleteOpen(true)}>
                  <Trash2 aria-hidden />
                  {messages.common.delete}
                </Button>
              )}
            </>
            )}
          </>
        }
      />

      <Card className="mb-4">
        <CardContent className="space-y-3 pt-4 lg:pt-5">
          <JobProgress
            label={`${formatNumber(counts.done + counts.skipped)} / ${formatNumber(counts.total)}`}
            done={counts.done + counts.skipped}
            total={counts.total}
            status={progress?.data.status ?? c.status}
          />
          <p className="tabular flex flex-wrap gap-x-3 gap-y-1 text-body">
            <span>成功 {counts.success}</span>
            <span>失敗 {counts.failed}</span>
            <span className={cn(counts.unknown > 0 && 'font-semibold')}>結果不明 {counts.unknown}</span>
            <span className={cn(counts.needsAction > 0 && 'font-semibold')}>要対応 {counts.needsAction}</span>
            <span>待機 {counts.pending + counts.holding}</span>
          </p>
          {c.sendImmediately && currentStatus === 'running' && (
            <p className="text-note font-semibold">{messages.campaign.sendNowActive}</p>
          )}
          {progress && (
            <p className="tabular text-note text-foreground-muted">
              本日 {progress.data.todaySent}/{c.sendImmediately ? '上限なし' : progress.data.dailyLimit}
              {progress.data.nextRunAt && `（次回 ${formatDateTime(progress.data.nextRunAt)}）`}
            </p>
          )}
        </CardContent>
      </Card>

      <Tabs.Root value={tab} onValueChange={setTab}>
        <Tabs.List className="scroll-x mb-3 flex gap-1 border-b border-border" aria-label="キャンペーンの情報">
          {TABS.map((item) => (
            <Tabs.Trigger
              key={item.value}
              value={item.value}
              className={tabTriggerClass}
            >
              {item.label}
              {item.value === 'targets' && <span className="tabular ml-1.5">{counts.total}</span>}
              {item.value === 'unknown' && counts.unknown > 0 && (
                <span className="tabular ml-1.5 font-semibold">{counts.unknown}</span>
              )}
              {item.value === 'needs_action' && counts.needsAction > 0 && (
                <span className="tabular ml-1.5 font-semibold">{counts.needsAction}</span>
              )}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value="targets" className="space-y-3">
          {isDraftLike ? (
            <DraftTargets preflight={preflight?.data} loading={preflightLoading} onSendNow={() => setSendNowOpen(true)} />
          ) : (
          <>
          <div className="scroll-x flex gap-2">
            {STATE_FILTERS.map((filter) => (
              <Button
                key={filter.value || 'all'}
                variant={stateFilter === filter.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStateFilter(filter.value)}
                className="shrink-0"
              >
                {filter.label}
              </Button>
            ))}
          </div>
          <TargetList
            columns={columns}
            rows={targets?.data ?? []}
            isLoading={targetsLoading}
            onOpenAttempt={setAttemptId}
          />
          </>
          )}
        </Tabs.Content>

        <Tabs.Content value="unknown">
          <p className="mb-3 text-body text-foreground-muted">
            送信を実行しましたが完了を確認できなかったものです。内容を確認して結果を確定してください。
          </p>
          <TargetList
            columns={columns}
            rows={targets?.data ?? []}
            isLoading={targetsLoading}
            onOpenAttempt={setAttemptId}
          />
        </Tabs.Content>

        <Tabs.Content value="needs_action">
          <p className="mb-3 text-body text-foreground-muted">
            CAPTCHA などで自動送信できなかったものです。フォーム URL と文面をコピーして手動で送信できます。
          </p>
          <NeedsActionList targets={targets?.data ?? []} isLoading={targetsLoading} />
        </Tabs.Content>

        <Tabs.Content value="messages" className="space-y-3">
          <CampaignMessages
            campaignId={id}
            status={progress?.data.status ?? c.status}
            canManage={hasRole('manager')}
          />
        </Tabs.Content>

        <Tabs.Content value="tracking">
          <CampaignTracking campaignId={id} enabled={tab === 'tracking'} />
        </Tabs.Content>

        <Tabs.Content value="settings">
          <Card>
            <CardContent className="pt-4 lg:pt-5">
              <dl className="divide-y divide-border">
                <SettingRow label="商材">{c.product?.name ?? '—'}</SettingRow>
                <SettingRow label="対象リスト">{c.list?.name ?? '—'}</SettingRow>
                <SettingRow label="チャネル">{ChannelPolicies.label(c.channelPolicy)}</SettingRow>
                <SettingRow label={messages.campaign.generationMode}>
                  {GenerationModes.label(
                    (c as unknown as { generationMode?: string }).generationMode ?? 'ai',
                  )}
                </SettingRow>
                <SettingRow label="承認要否">{ApprovalPolicies.label(c.approvalPolicy)}</SettingRow>
                <SettingRow label="1日あたり上限">{formatNumber(c.dailyLimit)} 社</SettingRow>
                <SettingRow label="全体上限">
                  {c.totalLimit ? `${formatNumber(c.totalLimit)} 社` : '対象全件'}
                </SettingRow>
                <SettingRow label="同一ドメイン間隔">
                  {(c as unknown as { domainIntervalHours: number }).domainIntervalHours} 時間
                </SettingRow>
                <SettingRow label="開始日時">{formatDateTime(c.scheduledAt)}</SettingRow>
                {c.stopReason && <SettingRow label="停止理由">{c.stopReason}</SettingRow>}
              </dl>
              {(c.status === 'running' || c.status === 'paused') && (
                <p className="mt-3 text-note text-foreground-muted">
                  実行中は 1 日あたり上限・全体上限・送信可能時間帯・同一ドメイン間隔のみ変更できます。
                </p>
              )}
            </CardContent>
          </Card>
        </Tabs.Content>
      </Tabs.Root>

      <ConfirmDialog
        open={pauseOpen}
        onOpenChange={setPauseOpen}
        title={messages.campaign.pause}
        summary={`「${c.name}」を一時停止します。`}
        detail={<p className="text-body text-foreground-muted">{messages.campaign.confirmPause}</p>}
        confirmText={messages.campaign.pause}
        onConfirm={() => pause.mutate()}
        isPending={pause.isPending}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={messages.campaign.remove}
        summary={messages.campaign.confirmRemove(c.name)}
        detail={<p className="text-body text-foreground-muted">{messages.campaign.removeNote}</p>}
        confirmText={messages.common.delete}
        irreversible
        onConfirm={() => remove.mutate()}
        isPending={remove.isPending}
      />

      <ConfirmDialog
        open={sendNowOpen}
        onOpenChange={setSendNowOpen}
        title={messages.campaign.sendNow}
        summary={messages.campaign.sendNowNote}
        detail={
          <div className="space-y-2 text-body">
            {isDraftLike ? (
              preflight ? (
                <dl className="tabular space-y-1">
                  <SummaryRow label="対象">{formatNumber(preflight.data.sendable)} 社</SummaryRow>
                  <SummaryRow label="除外">
                    {formatNumber(preflight.data.blocked.reduce((sum, b) => sum + b.count, 0))} 社
                  </SummaryRow>
                  <SummaryRow label="未承認">{formatNumber(preflight.data.notApproved)} 社（承認されるまで送りません）</SummaryRow>
                </dl>
              ) : (
                <p className="text-foreground-muted">送信前チェックを実行しています…</p>
              )
            ) : (
              <p>待機中の {formatNumber(counts.pending + counts.holding)} 社に今から順に送ります。</p>
            )}
            <p className="text-note text-foreground-muted">{messages.campaign.sendNowRateNote}</p>
          </div>
        }
        confirmText={messages.campaign.sendNow}
        onConfirm={() => sendNow.mutate()}
        isPending={sendNow.isPending || (isDraftLike && !preflight)}
      />

      <CampaignRenameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        campaignId={id}
        currentName={c.name}
      />

      <SendAttemptDialog
        attemptId={attemptId}
        onOpenChange={(open) => !open && setAttemptId(null)}
      />
    </>
  );
};

const SummaryRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex gap-3">
    <dt className="w-24 shrink-0 text-foreground-muted">{label}</dt>
    <dd className="min-w-0 flex-1">{children}</dd>
  </div>
);

/** 開始前の「対象」タブ。確定前なので送信前チェックの内訳を見せる */
const DraftTargets = ({
  preflight,
  loading,
  onSendNow,
}: {
  preflight?: Preflight;
  loading: boolean;
  onSendNow: () => void;
}) => {
  if (loading || !preflight) return <SkeletonRows rows={3} />;
  const blocked = preflight.blocked.reduce((sum, b) => sum + b.count, 0);
  return (
    <Card>
      <CardContent className="space-y-3 pt-4 lg:pt-5">
        <p className="text-h3">{messages.campaign.draftTargetsTitle}</p>
        <p className="text-note text-foreground-muted">{messages.campaign.draftTargetsNote}</p>
        <dl className="tabular space-y-1 text-body">
          <SummaryRow label="対象候補">{formatNumber(preflight.totalCandidates)} 社</SummaryRow>
          <SummaryRow label="送信可能">{formatNumber(preflight.sendable)} 社</SummaryRow>
          <SummaryRow label="除外">
            {formatNumber(blocked)} 社
            {blocked > 0 && `（${preflight.blocked.filter((b) => b.count > 0).map((b) => `${b.label} ${b.count}`).join('・')}）`}
          </SummaryRow>
          <SummaryRow label="未承認">{formatNumber(preflight.notApproved)} 社</SummaryRow>
        </dl>
        {preflight.warnings.length > 0 && (
          <ul className="space-y-1 text-note text-foreground-muted">
            {preflight.warnings.map((w) => (
              <li key={w}>・{w}</li>
            ))}
          </ul>
        )}
        <Button onClick={onSendNow}>
          <Zap aria-hidden />
          {messages.campaign.sendNow}
        </Button>
      </CardContent>
    </Card>
  );
};

const TargetList = ({
  columns,
  rows,
  isLoading,
  onOpenAttempt,
}: {
  columns: Column<CampaignTarget & { skipReasonLabel: string | null }>[];
  rows: (CampaignTarget & { skipReasonLabel: string | null })[];
  isLoading: boolean;
  onOpenAttempt: (id: string) => void;
}) => (
  <ResponsiveList
    columns={columns}
    rows={rows}
    isLoading={isLoading}
    onRowClick={(row) => row.lastSendAttemptId && onOpenAttempt(row.lastSendAttemptId)}
    empty={<EmptyState title="該当する対象がありません" />}
    card={(row) => (
      <button
        type="button"
        className="w-full space-y-1.5 text-left"
        onClick={() => row.lastSendAttemptId && onOpenAttempt(row.lastSendAttemptId)}
      >
        <p className="truncate text-body font-semibold">{row.companyName}</p>
        <div className="flex flex-wrap items-center gap-2">
          {row.lastResult ? (
            <StatusBadge kind="send_result" value={row.lastResult} />
          ) : (
            <StatusBadge kind="target_state" value={row.state} />
          )}
          {row.channel && <StatusBadge kind="channel" value={row.channel} />}
        </div>
        {row.skipReasonLabel && (
          <p className="text-note text-foreground-muted">↳ {row.skipReasonLabel}</p>
        )}
        <p className="tabular text-note text-foreground-subtle">{formatDateTime(row.lastAttemptAt)}</p>
      </button>
    )}
  />
);

/** 要対応：フォーム URL と文面をコピーして手動送信し、結果を記録する */
const NeedsActionList = ({
  targets,
  isLoading,
}: {
  targets: (CampaignTarget & { skipReasonLabel: string | null })[];
  isLoading: boolean;
}) => {
  const queryClient = useQueryClient();

  const markSent = useMutation({
    mutationFn: (attemptId: string) =>
      api.post(`/send-attempts/${attemptId}/mark-result`, {
        result: 'success',
        note: '手動で送信し、完了を確認しました',
      }),
    onSuccess: () => {
      toast.success('送信済みとして記録しました');
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (isLoading) return <SkeletonRows rows={3} />;
  if (targets.length === 0) return <EmptyState title="要対応の対象はありません" />;

  return (
    <ul className="space-y-3">
      {targets.map((target) => (
        <li key={target.id}>
          <Card>
            <CardContent className="space-y-2 pt-4 lg:pt-5">
              <p className="text-body font-semibold">{target.companyName}</p>
              {target.lastErrorCode && (
                <p className="text-body text-foreground-muted">
                  ↳ {SEND_ERROR_LABELS[target.lastErrorCode as SendErrorCode] ?? target.lastErrorCode}
                </p>
              )}
              {target.contactValue && (
                <p className="break-all text-note text-foreground-muted">{target.contactValue}</p>
              )}
              <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                {target.contactValue && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void navigator.clipboard.writeText(target.contactValue!);
                        toast.success(messages.common.copied);
                      }}
                    >
                      <Copy aria-hidden className="size-4" />
                      {messages.campaign.copyFormUrl}
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href={target.contactValue} target="_blank" rel="noreferrer noopener">
                        <ExternalLink aria-hidden className="size-4" />
                        フォームを開く
                      </a>
                    </Button>
                  </>
                )}
                {target.generatedMessageId && (
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/messages/${target.generatedMessageId}`}>{messages.campaign.copyMessage}</Link>
                  </Button>
                )}
                {target.lastSendAttemptId && (
                  <Button
                    size="sm"
                    onClick={() => markSent.mutate(target.lastSendAttemptId!)}
                    disabled={markSent.isPending}
                  >
                    {messages.campaign.markSent}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
};

type CampaignMessageRow = {
  id: string;
  companyName: string;
  subject: string | null;
  status: string;
  ngPhraseHits: string[];
  unresolvedKeys: string[];
};

/** 文面タブ。一覧・まとめて承認／削除・作り直し */
const CampaignMessages = ({
  campaignId,
  status,
  canManage,
}: {
  campaignId: string;
  status: string;
  canManage: boolean;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selection = useSelection();
  const [deleteTarget, setDeleteTarget] = useState<'selected' | 'all' | null>(null);
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: qk.messages({ campaignId }),
    queryFn: () =>
      api.get<ListResponse<CampaignMessageRow>>('/messages', { campaignId, limit: 100 }),
  });
  const rows = data?.data ?? [];
  const unresolvedCount = rows.filter((r) => r.unresolvedKeys.length > 0).length;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['messages'] });
    void queryClient.invalidateQueries({ queryKey: qk.campaign(campaignId) });
  };

  const approveAll = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ applied: number; skipped: number; reasons: Record<string, number> }>>(
        '/messages/bulk-approve',
        { campaignId },
      ),
    onSuccess: (result) => {
      const reasons = result.data.reasons ?? {};
      const notes = [
        reasons.ng_phrase ? `禁止表現 ${reasons.ng_phrase} 件` : null,
        reasons.unresolved_template ? `差し込み未完了 ${reasons.unresolved_template} 件` : null,
      ].filter(Boolean);
      toast.success(`${formatNumber(result.data.applied)} 件を承認しました`, {
        description: result.data.skipped > 0 ? `${result.data.skipped} 件は見送りました（${notes.join('・')}）` : undefined,
      });
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (target: 'selected' | 'all') =>
      api.post<SingleResponse<{ deleted: number; skipped: number }>>('/messages/bulk-delete', {
        campaignId,
        ...(target === 'selected' ? { messageIds: [...selection.selected] } : {}),
      }),
    onSuccess: (result) => {
      toast.success(`${formatNumber(result.data.deleted)} 件の文面を削除しました`, {
        description:
          result.data.skipped > 0 ? `${result.data.skipped} 件は送信済みのため残しました` : undefined,
      });
      selection.clear();
      setDeleteTarget(null);
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const regenerate = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ jobRunId: string; total: number }>>('/messages/bulk-generate', {
        campaignId,
      }),
    onSuccess: (result) => {
      toast.success(`${formatNumber(result.data.total)} 社分の文面の生成を開始しました`);
      setRegenerateOpen(false);
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const canRegenerate = ['draft', 'scheduled', 'paused'].includes(status);

  const columns: Column<CampaignMessageRow>[] = [
    {
      key: 'company',
      header: '企業名',
      width: '200px',
      render: (row) => <span className="truncate font-semibold">{row.companyName}</span>,
    },
    {
      key: 'subject',
      header: '件名',
      render: (row) => (
        <span className="truncate text-foreground-muted">{row.subject ?? '（件名なし）'}</span>
      ),
    },
    {
      key: 'status',
      header: '状態',
      width: '220px',
      render: (row) => <MessageFlags row={row} />,
    },
  ];

  return (
    <div className="space-y-3">
      {canManage && (
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => approveAll.mutate()} disabled={approveAll.isPending || rows.length === 0}>
            {messages.message.bulkApprove}
          </Button>
          {canRegenerate && (
            <Button variant={rows.length === 0 ? 'default' : 'outline'} onClick={() => setRegenerateOpen(true)}>
              {rows.length === 0 ? messages.message.generate : messages.campaign.regenerateAll}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setDeleteTarget('all')}
            disabled={rows.length === 0}
          >
            <Trash2 aria-hidden />
            すべての文面を削除する
          </Button>
        </div>
      )}

      {unresolvedCount > 0 && (
        <p className="text-note text-foreground-muted">
          差し込みが残っている文面が {formatNumber(unresolvedCount)} 件あります。承認・送信できないため、
          設定やセキュリティチェックを確認してから「{messages.campaign.regenerateAll}」で作り直してください。
        </p>
      )}

      <ResponsiveList
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        selection={
          canManage
            ? {
                selected: selection.selected,
                onToggle: selection.toggle,
                onToggleAll: (checked) => selection.toggleAll(rows.map((r) => r.id), checked),
              }
            : undefined
        }
        onRowClick={(row) => navigate(`/messages/${row.id}`)}
        empty={
          <EmptyState
            title="文面はまだ生成されていません"
            description="キャンペーンの設定（テンプレート差し込み、または AI）に沿って、対象の企業ごとに作成します"
            action={canRegenerate ? { label: messages.message.generate, onClick: () => setRegenerateOpen(true) } : undefined}
          />
        }
        card={(row) => (
          <button
            type="button"
            className="w-full space-y-1.5 text-left"
            onClick={() => navigate(`/messages/${row.id}`)}
          >
            <p className="truncate text-body font-semibold">{row.companyName}</p>
            <p className="truncate text-note text-foreground-muted">{row.subject ?? '（件名なし）'}</p>
            <MessageFlags row={row} />
          </button>
        )}
      />

      {canManage && (
        <BulkBar count={selection.count} onClear={selection.clear}>
          <Button variant="outline" size="sm" onClick={() => setDeleteTarget('selected')}>
            <Trash2 aria-hidden className="size-4" />
            選択した文面を削除する
          </Button>
        </BulkBar>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="文面を削除する"
        summary={
          deleteTarget === 'selected'
            ? `選択した ${formatNumber(selection.count)} 件の文面を削除します。`
            : `このキャンペーンの文面 ${formatNumber(rows.length)} 件をすべて削除します。`
        }
        detail={
          <p className="text-body text-foreground-muted">
            送信済みの文面は履歴として残り、削除されません。削除した文面は元に戻せません。
          </p>
        }
        confirmText={messages.common.delete}
        irreversible
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget)}
        isPending={remove.isPending}
      />

      <ConfirmDialog
        open={regenerateOpen}
        onOpenChange={setRegenerateOpen}
        title={rows.length === 0 ? messages.message.generate : messages.campaign.regenerateAll}
        summary={
          rows.length === 0
            ? 'キャンペーンの設定（テンプレート差し込み、または AI）に沿って、対象の企業ごとに文面を作成します。完了すると通知でお知らせします。'
            : messages.campaign.confirmRegenerateAll(rows.length)
        }
        detail={
          <p className="text-body text-foreground-muted">
            企業サイト・署名・セキュリティチェックの現在の値で差し込み直します。
          </p>
        }
        confirmText={rows.length === 0 ? messages.message.generate : messages.campaign.regenerateAll}
        onConfirm={() => regenerate.mutate()}
        isPending={regenerate.isPending}
      />
    </div>
  );
};

/** 文面の状態と、承認できない理由（禁止表現・差し込み未完了） */
const MessageFlags = ({ row }: { row: CampaignMessageRow }) => (
  <span className="flex flex-wrap items-center gap-1.5">
    <StatusBadge kind="message_status" value={row.status} />
    {row.ngPhraseHits.length > 0 && (
      <span className="rounded-control border-2 border-foreground px-1.5 text-note font-semibold">
        禁止表現
      </span>
    )}
    {row.unresolvedKeys.length > 0 && (
      <span
        className="rounded-control border-2 border-dashed border-foreground px-1.5 text-note font-semibold"
        title={row.unresolvedKeys.map((k) => `{{${k}}}`).join(' ')}
      >
        差し込み未完了
      </span>
    )}
  </span>
);

const SettingRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex gap-4 py-2.5">
    <dt className="w-40 shrink-0 text-note text-foreground-muted">{label}</dt>
    <dd className="tabular min-w-0 flex-1 text-body">{children}</dd>
  </div>
);

/** 効果測定（開封・クリック）。メールで送った分だけが対象になる */
const CampaignTracking = ({ campaignId, enabled }: { campaignId: string; enabled: boolean }) => {
  const { data, isLoading } = useQuery({
    queryKey: qk.campaignTracking(campaignId),
    queryFn: () => api.get<SingleResponse<TrackingStats>>(`/campaigns/${campaignId}/tracking`),
    enabled,
  });

  if (isLoading) return <SkeletonRows rows={3} />;
  const stats = data?.data;
  if (!stats || stats.trackedSends === 0) {
    return (
      <EmptyState
        title={messages.tracking.noEvents}
        description="効果測定はメール送信のみが対象です。設定 → メール送信で開封・クリックの計測を有効にしてください。"
      />
    );
  }

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="pt-4 lg:pt-5">
          <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <TrackingStat label={messages.tracking.trackedSends} value={`${formatNumber(stats.trackedCompanies)} 社`} />
            <TrackingStat
              label={messages.tracking.openRate}
              value={formatRate(stats.openRate)}
              note={`${formatNumber(stats.openedCompanies)} 社`}
            />
            <TrackingStat
              label={messages.tracking.clickRate}
              value={formatRate(stats.clickRate)}
              note={`${formatNumber(stats.clickedCompanies)} 社`}
            />
            <TrackingStat
              label={messages.tracking.clickThroughRate}
              value={formatRate(stats.clickThroughRate)}
              note="開封した企業のうち"
            />
            <TrackingStat label={messages.tracking.delivered} value={`${formatNumber(stats.deliveredCompanies)} 社`} />
            <TrackingStat label={messages.tracking.bounced} value={`${formatNumber(stats.bouncedCompanies)} 社`} />
            <TrackingStat
              label={messages.tracking.unsubscribed}
              value={`${formatNumber(stats.unsubscribedCompanies)} 社`}
            />
            <TrackingStat
              label={messages.tracking.complained}
              value={`${formatNumber(stats.complainedCompanies)} 社`}
            />
          </dl>
          <p className="mt-3 text-note text-foreground-muted">{OPEN_TRACKING_NOTE}</p>
        </CardContent>
      </Card>

      {stats.topLinks.length > 0 && (
        <Card>
          <CardContent className="pt-4 lg:pt-5">
            <p className="mb-2 text-body font-semibold">{messages.tracking.topLinks}</p>
            <p className="mb-2 text-note text-foreground-muted">URL を押すと、クリックした企業が表示されます</p>
            <ul className="divide-y divide-border">
              {stats.topLinks.map((link) => (
                <TrackingLinkRow key={link.url} campaignId={campaignId} link={link} />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/** 効果測定の URL 行。開くとクリックした企業を一覧し、企業名から企業詳細へ飛べる */
const TrackingLinkRow = ({
  campaignId,
  link,
}: {
  campaignId: string;
  link: TrackingStats['topLinks'][number];
}) => {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['campaigns', campaignId, 'tracking', 'clicks', link.url],
    queryFn: () =>
      api.get<SingleResponse<TrackingLinkClicks>>(`/campaigns/${campaignId}/tracking/clicks`, { url: link.url }),
    enabled: open,
  });

  return (
    <li className="py-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-2 text-left hover:bg-muted"
      >
        <span className="min-w-0 flex-1 break-all text-body underline decoration-border-strong underline-offset-4">
          {link.kind === 'unsubscribe' ? messages.tracking.unsubscribeLink : link.url}
        </span>
        {link.kind === 'unsubscribe' && (
          <span className="w-full text-note text-foreground-muted">{messages.tracking.unsubscribeLinkNote}</span>
        )}
        <span className="tabular text-note text-foreground-muted">
          {formatNumber(link.companies)} 社・{formatNumber(link.clicks)} 回
        </span>
      </button>
      {open && (
        <div className="mt-2 pl-3">
          {isLoading || !data ? (
            <SkeletonRows rows={2} />
          ) : data.data.companies.length === 0 ? (
            <p className="text-note text-foreground-muted">クリックした企業はありません</p>
          ) : (
            <ul className="space-y-1">
              {data.data.companies.map((company) => (
                <li key={company.id} className="flex flex-wrap items-center gap-2 text-body">
                  <Link to={`/companies/${company.id}`} className="font-semibold underline-offset-4 hover:underline">
                    {company.name}
                  </Link>
                  <span className="tabular text-note text-foreground-muted">
                    {formatNumber(company.clicks)} 回・最終 {formatDateTime(company.lastClickedAt)}
                  </span>
                  {company.unsubscribed && (
                    <span className="rounded-control border-2 border-foreground px-1.5 text-note font-semibold">
                      {messages.tracking.unsubscribedDone}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

const TrackingStat = ({ label, value, note }: { label: string; value: string; note?: string }) => (
  <div>
    <dt className="text-note text-foreground-muted">{label}</dt>
    <dd className="tabular text-h2-sm font-semibold lg:text-h2">{value}</dd>
    {note && <p className="tabular text-note text-foreground-muted">{note}</p>}
  </div>
);
